import { Injectable } from '@angular/core';
import { AuthService } from '@auth';
import { OnlineOfflineService } from './online-offine.service';
import { PreferredOrganizationService } from './preferred-organization.service';

/**
 * Decides where reads come from and whether responses may be cached, relative to the
 * browser's preferred organization (US 27702).
 *
 * Preferred tenant active:  IndexedDB-first exactly like the app always behaved
 *                           (local wins when offline or when a local copy exists).
 * Any other tenant active:  server only. IndexedDB holds the PREFERRED tenant's data,
 *                           so reading it — or caching responses into it — under another
 *                           tenant would serve or store the wrong organization's data.
 */
@Injectable({
    providedIn: 'root'
})
export class DataSourceService {

    constructor(
        private authService: AuthService,
        private onlineOfflineService: OnlineOfflineService,
        private preferredOrganizationService: PreferredOrganizationService
    ) { }

    /** True while the active tenant is the browser's preferred organization. */
    public get canUseLocalData(): boolean {
        const preferred = this.preferredOrganizationService.preferred;
        // No preferred organization recorded yet: a fresh browser, or a pre-feature
        // install whose async migration has not resolved. Behave exactly as the app
        // always did (local-first) — a fresh browser's empty tables fall through to
        // HTTP anyway, and an upgraded install keeps working offline meanwhile.
        if (!preferred) return true;
        return preferred.tenantId === this.authService.currentTenant;
    }

    /** Alias that names the write-side decision at call sites gating local caching. */
    public get canCacheLocally(): boolean {
        return this.canUseLocalData;
    }

    /**
     * Replaces the historical `!isOnline || count > 0` read decision. `hasLocalCopy`
     * is the local row count (or any truthy evidence a local copy exists).
     */
    public useLocalData(hasLocalCopy: number | boolean): boolean {
        if (!this.canUseLocalData) return false;

        const localCopyExists = typeof hasLocalCopy === 'number' ? hasLocalCopy > 0 : !!hasLocalCopy;
        return !this.onlineOfflineService.isOnline || localCopyExists;
    }
}
